import { useEffect, useMemo, useState } from "react";
import {
    X, Loader2, Eye, Heart, Bookmark, Share2, MessageCircle, Phone,
} from "lucide-react";
import { api, fileUrl } from "../../lib/api";

const WA_ICON = (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 2.09.66 4.03 1.79 5.62L2 22l4.62-1.87c1.53.83 3.28 1.31 5.11 1.31h.31c5.46 0 9.9-4.44 9.9-9.9 0-2.65-1.03-5.14-2.9-7.02C17.16 2.65 14.67 2 12.04 2z" />
    </svg>
);

/** Slide-up analytics sheet for a single reel. Shows totals + 14-day daily chart + recent enquiries. */
export default function ReelAnalyticsSheet({ reel, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(14);

    useEffect(() => {
        setLoading(true);
        api.get(`/me/reels/${reel.id}/analytics`, { params: { days } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [reel.id, days]);

    const maxDaily = useMemo(() => {
        if (!data?.daily?.length) return 1;
        return Math.max(1, ...data.daily.map((d) => d.views + d.enquiries * 3 + d.calls * 3));
    }, [data]);

    return (
        <div className="fixed inset-0 z-40 flex items-end" style={{ background: "rgba(0,0,0,0.6)" }}
             onClick={onClose} data-testid="reel-analytics-sheet">
            <div className="w-full bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white px-4 pt-3 pb-2 border-b flex items-center gap-3"
                     style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                    <div className="w-10 h-14 rounded-md overflow-hidden bg-black shrink-0">
                        {reel.thumbnail_url ? (
                            <img src={fileUrl(reel.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <video src={fileUrl(reel.video_url)} muted playsInline className="w-full h-full object-cover" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-heading font-extrabold text-[13px] truncate" style={{ color: "#002657" }}>
                            {reel.property?.title || "Reel"}
                        </div>
                        <div className="text-[10.5px]" style={{ color: "rgba(0,38,87,0.6)" }}>
                            Analytics · last {days} days
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="reel-analytics-close"
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: "#f4f4f5" }}>
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {loading || !data ? (
                    <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#002657" }} /></div>
                ) : (
                    <>
                        {/* Range picker */}
                        <div className="px-4 pt-3 flex gap-1.5">
                            {[7, 14, 30].map((d) => (
                                <button key={d} onClick={() => setDays(d)}
                                    data-testid={`reel-analytics-range-${d}`}
                                    className="px-2.5 h-7 rounded-full text-[11px] font-extrabold border"
                                    style={{
                                        background: days === d ? "linear-gradient(90deg, #DAA520, #F59E0B)" : "white",
                                        color: days === d ? "#001a3d" : "#002657",
                                        borderColor: "rgba(0,38,87,0.1)",
                                    }}>
                                    {d}d
                                </button>
                            ))}
                        </div>

                        {/* Total stat grid */}
                        <div className="px-4 mt-3 grid grid-cols-3 gap-2" data-testid="reel-analytics-totals">
                            <Stat icon={<Eye className="w-3.5 h-3.5" />}         label="Views"     value={data.totals.views}     tint="#DBEAFE" fg="#1E40AF" />
                            <Stat icon={<Heart className="w-3.5 h-3.5" />}       label="Likes"     value={data.totals.likes}     tint="#FEE2E2" fg="#B91C1C" />
                            <Stat icon={<Bookmark className="w-3.5 h-3.5" />}    label="Saves"     value={data.totals.saves}     tint="#F5F3FF" fg="#6D28D9" />
                            <Stat icon={<MessageCircle className="w-3.5 h-3.5" />} label="Enquiries" value={data.totals.enquiries} tint="#FEF3C7" fg="#B45309" hero />
                            <Stat icon={<Phone className="w-3.5 h-3.5" />}       label="Calls"     value={data.totals.calls}     tint="#D1FAE5" fg="#065F46" hero />
                            <Stat icon={WA_ICON}                                 label="WhatsApp"  value={data.totals.whatsapp}  tint="#DCFCE7" fg="#166534" hero />
                        </div>

                        {/* Daily chart */}
                        <div className="px-4 mt-4">
                            <div className="text-[10.5px] uppercase tracking-widest font-extrabold mb-2"
                                 style={{ color: "#8B6712" }}>Daily activity</div>
                            <div className="rounded-2xl bg-white border p-3" style={{ borderColor: "rgba(0,38,87,0.08)" }}
                                 data-testid="reel-analytics-chart">
                                <div className="flex items-end gap-1 h-28">
                                    {data.daily.map((d) => {
                                        const total = d.views + d.likes + d.enquiries + d.calls + d.whatsapp;
                                        const ratio = maxDaily ? Math.min(1, (d.views + d.enquiries * 3 + d.calls * 3) / maxDaily) : 0;
                                        const h = Math.max(4, Math.round(ratio * 100));
                                        return (
                                            <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative">
                                                {total > 0 && (
                                                    <div className="absolute -top-6 hidden group-hover:block bg-navy-900 text-white text-[9px] px-1.5 py-0.5 rounded"
                                                         style={{ background: "#001a3d" }}>
                                                        {total}
                                                    </div>
                                                )}
                                                <div className="w-full rounded-t"
                                                     style={{
                                                         height: `${h}%`,
                                                         background: total > 0
                                                             ? "linear-gradient(180deg, #F59E0B, #DAA520)"
                                                             : "rgba(0,38,87,0.06)",
                                                     }}
                                                     title={`${d.date}: ${total} activity`} />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-2 flex justify-between text-[9px]" style={{ color: "rgba(0,38,87,0.5)" }}>
                                    <span>{data.daily[0]?.date?.slice(5)}</span>
                                    <span>{data.daily[data.daily.length - 1]?.date?.slice(5)}</span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-[10px]" style={{ color: "rgba(0,38,87,0.7)" }}>
                                    <Legend color="#F59E0B" label="Weighted activity (views + 3× enquiries + 3× calls)" />
                                </div>
                            </div>
                        </div>

                        {/* Daily table */}
                        <div className="px-4 mt-4" data-testid="reel-analytics-table">
                            <div className="rounded-2xl bg-white border overflow-hidden"
                                 style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                                <div className="grid grid-cols-6 text-[10px] font-extrabold px-3 py-2"
                                     style={{ background: "#FBF6E0", color: "#8B6712" }}>
                                    <div>Day</div>
                                    <div className="text-right">Views</div>
                                    <div className="text-right">Likes</div>
                                    <div className="text-right">Enq.</div>
                                    <div className="text-right">Calls</div>
                                    <div className="text-right">WA</div>
                                </div>
                                {data.daily.slice().reverse().slice(0, 14).map((d) => (
                                    <div key={d.date} className="grid grid-cols-6 text-[10.5px] px-3 py-2 border-t"
                                         style={{ borderColor: "rgba(0,38,87,0.05)", color: "#002657" }}>
                                        <div className="font-bold">{d.date.slice(5)}</div>
                                        <div className="text-right">{d.views}</div>
                                        <div className="text-right">{d.likes}</div>
                                        <div className="text-right font-extrabold" style={{ color: d.enquiries > 0 ? "#B45309" : "inherit" }}>{d.enquiries}</div>
                                        <div className="text-right font-extrabold" style={{ color: d.calls > 0 ? "#065F46" : "inherit" }}>{d.calls}</div>
                                        <div className="text-right font-extrabold" style={{ color: d.whatsapp > 0 ? "#166534" : "inherit" }}>{d.whatsapp}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent enquiries */}
                        <div className="px-4 mt-4 pb-6" data-testid="reel-analytics-recent">
                            <div className="text-[10.5px] uppercase tracking-widest font-extrabold mb-2"
                                 style={{ color: "#8B6712" }}>Recent enquiries from this reel</div>
                            {data.recent_enquiries.length === 0 ? (
                                <div className="rounded-2xl bg-white border p-4 text-center text-[11.5px]"
                                     style={{ borderColor: "rgba(0,38,87,0.08)", color: "rgba(0,38,87,0.6)" }}>
                                    No enquiries yet from this reel.
                                </div>
                            ) : (
                                <div className="rounded-2xl bg-white border divide-y overflow-hidden"
                                     style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                                    {data.recent_enquiries.map((e) => (
                                        <div key={e.id} className="p-3 flex items-start gap-2 divide-navy-500/5">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                                                 style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                                                {(e.name || "?").slice(0, 1).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="font-heading font-extrabold text-[12px]" style={{ color: "#002657" }}>{e.name}</div>
                                                    <div className="text-[10px] px-1.5 py-0.5 rounded"
                                                         style={{ background: "#FBF6E0", color: "#8B6712" }}>{e.status || "new"}</div>
                                                </div>
                                                <div className="text-[10.5px]" style={{ color: "rgba(0,38,87,0.65)" }}>
                                                    {e.phone} · {e.email}
                                                </div>
                                                {e.message && (
                                                    <div className="mt-1 text-[10.5px] line-clamp-2" style={{ color: "rgba(0,38,87,0.85)" }}>{e.message}</div>
                                                )}
                                                <div className="mt-1 text-[9.5px]" style={{ color: "rgba(0,38,87,0.45)" }}>
                                                    {(e.created_at || "").slice(0, 16).replace("T", " ")}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ icon, label, value, tint, fg, hero }) {
    return (
        <div className="rounded-xl p-2.5 border"
             style={{ background: tint, borderColor: hero ? fg : "transparent" }}
             data-testid={`reel-analytics-stat-${label.toLowerCase()}`}>
            <div className="flex items-center gap-1 text-[10px] font-extrabold" style={{ color: fg }}>
                {icon} {label}
            </div>
            <div className="mt-1 font-heading font-extrabold text-[18px]" style={{ color: fg }}>{value}</div>
        </div>
    );
}

function Legend({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} /> {label}
        </span>
    );
}
